/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import { FormControl, IconButton, InputAdornment, Select } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import api from "../../api";
import DropdownSearchBar from "../dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader";
import { SelectedMenuItem } from "../selected_menu_item";
import { StyledMenuItem } from "../styled_menu_item";
import { Query_Keys } from "../../lib/queryKeys";
import { PerPage } from "../../constants/perPage";
import { useDebounce } from "../../utils/useDebounce";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { ALL_FILTER_VALUE } from "../../utils/perqReportDrilldown";
import { selectBaseStyles } from "../../pages/reports/classified_discount_records/styles";
import { dropdownMenuProps } from "../../pages/reports/perq_report/styles";

export const defaultRestaurantScope = () => ({
  restaurant_group_id: ALL_FILTER_VALUE,
  restaurant_sub_group_id: ALL_FILTER_VALUE,
  restaurant_ids: [ALL_FILTER_VALUE],
});

/**
 * Strips the "All" sentinels so an unselected filter is omitted from the request entirely.
 * Sending an empty `restaurant_ids` would scope a report to nothing server-side.
 */
export const scopeToParams = (scope = {}) => {
  const restaurantIds = (scope.restaurant_ids || []).filter((id) => id !== ALL_FILTER_VALUE);

  return {
    restaurant_group_id:
      scope.restaurant_group_id === ALL_FILTER_VALUE ? undefined : scope.restaurant_group_id,
    restaurant_sub_group_id:
      scope.restaurant_sub_group_id === ALL_FILTER_VALUE ? undefined : scope.restaurant_sub_group_id,
    restaurant_ids: restaurantIds.length ? restaurantIds : undefined,
  };
};

/**
 * The group / sub-group / restaurant scope selectors shared by the Sales reports. Fully
 * controlled: `value` is the current scope and every change emits a complete next scope,
 * including the cascade resets a parent change implies.
 *
 * Search text and the loaded option pages are internal. A parent that needs them cleared
 * (a Reset button) should remount via `key` rather than reach in.
 */
export const RestaurantScopeFilters = ({ source, testIdPrefix, value, onChange }) => {
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([]);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);

  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);

  const hasGroupSelected = value.restaurant_group_id !== ALL_FILTER_VALUE;
  const hasSubGroupSelected = value.restaurant_sub_group_id !== ALL_FILTER_VALUE;

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source,
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantGroupsPage,
      hasNextPage: hasNextRestaurantGroupsPage,
      isFetchingNextPage: restaurantGroupsFetchingNextPage,
    });

  const fetchRestaurantSubGroups = async ({ pageParam = 1 }) => {
    if (!hasGroupSelected) return [];
    const response = await api.get("/v1/admin/dropdowns/restaurant_sub_groups_list", {
      params: {
        restaurant_group_id: value.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
      },
    });
    return response?.data || [];
  };

  const {
    data: restaurantSubGroups = [],
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: value.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
      source,
    }),
    queryFn: fetchRestaurantSubGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: hasGroupSelected,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantSubGroupsPage,
      hasNextPage: hasNextRestaurantSubGroupsPage,
      isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: hasGroupSelected ? value.restaurant_group_id : undefined,
        restaurant_sub_group_id: hasSubGroupSelected ? value.restaurant_sub_group_id : undefined,
      },
    });
    return (
      response?.data?.map((record) => ({
        ...normalizeRestaurant(record),
        id: Number(record?.value ?? record?.id),
      })) || []
    );
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: value.restaurant_group_id,
      restaurant_sub_group_id: value.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
      source,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantsPage,
      hasNextPage: hasNextRestaurantsPage,
      isFetchingNextPage: restaurantsFetchingNextPage,
    });

  useEffect(() => {
    setRestaurantGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantGroups, "label", ["value"]),
    );
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantSubGroups, "value", ["label"]),
    );
  }, [restaurantSubGroups]);

  useEffect(() => {
    setRestaurantOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurants, "id", ["name"]),
    );
  }, [restaurants]);

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${value.restaurant_group_id}`,
  );
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], "label")
      : restaurantGroups;

  const selectedSubGroupOption = restaurantSubGroupOptionsCache?.find(
    (subGroup) => `${subGroup?.value}` === `${value.restaurant_sub_group_id}`,
  );
  const displayedSubGroups = restaurantSubGroupSearch.trim()
    ? restaurantSubGroups
    : selectedSubGroupOption
      ? mergeUniqueByKey(restaurantSubGroups, [selectedSubGroupOption], "value")
      : restaurantSubGroups;

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) =>
    value.restaurant_ids?.some((id) => `${id}` === `${restaurant?.id}`),
  );
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, "id");

  // Groups arrive with the id in `label` and the name in `value`, inverted from the others.
  const getRestaurantGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All restaurant groups";
    const cached = restaurantGroupOptionsCache?.find((group) => `${group?.label}` === `${id}`);
    if (cached?.value) return cached.value;
    return (
      restaurantGroups?.find((record) => `${record.label}` === `${id}`)?.value ||
      "All restaurant groups"
    );
  };

  const getSubGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All sub-groups";
    const cached = restaurantSubGroupOptionsCache?.find((sg) => `${sg?.value}` === `${id}`);
    if (cached?.label) return cached.label;
    return (
      restaurantSubGroups?.find((record) => `${record.value}` === `${id}`)?.label || "All sub-groups"
    );
  };

  const getRestaurantDisplayValue = () => {
    if (!value.restaurant_ids?.length || value.restaurant_ids.includes(ALL_FILTER_VALUE)) {
      return "All restaurants";
    }
    if (value.restaurant_ids.length === 1) {
      const restaurant = restaurantOptionsCache?.find(
        (record) => `${record.id}` === `${value.restaurant_ids[0]}`,
      );
      return restaurant?.name || "All restaurants";
    }
    return `${value.restaurant_ids.length} Restaurants Selected`;
  };

  // A narrower parent scope invalidates whatever was chosen below it, so the cascade always
  // resets its descendants rather than leaving a sub-group that the new group does not own.
  const emitGroup = (groupId) => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    onChange({
      restaurant_group_id: groupId,
      restaurant_sub_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
    });
  };

  const emitSubGroup = (subGroupId) => {
    setRestaurantSearch("");
    onChange({
      ...value,
      restaurant_sub_group_id: subGroupId,
      restaurant_ids: [ALL_FILTER_VALUE],
    });
  };

  const handleRestaurantChange = (event) => {
    const selected = event.target.value;
    const isCleared =
      selected.length === 0 || (selected.length === 1 && selected.includes(ALL_FILTER_VALUE));

    onChange({
      ...value,
      restaurant_ids: isCleared
        ? [ALL_FILTER_VALUE]
        : selected.filter((record) => record !== ALL_FILTER_VALUE),
    });
  };

  const clearAdornment = (visible, onClear) =>
    visible ? (
      <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
        <IconButton edge="end" size="small" disableRipple onClick={onClear}>
          <ClearIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      </InputAdornment>
    ) : null;

  return (
    <React.Fragment>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "185px" } }}>
        <Select
          data-testid={`${testIdPrefix}-restaurant-group`}
          value={value.restaurant_group_id}
          renderValue={() => getRestaurantGroupName(value.restaurant_group_id)}
          onChange={(event) => emitGroup(event.target.value)}
          onClose={() => setTimeout(() => setRestaurantGroupSearch(""), 250)}
          sx={selectBaseStyles}
          endAdornment={clearAdornment(hasGroupSelected, () => emitGroup(ALL_FILTER_VALUE))}
          MenuProps={dropdownMenuProps(restaurantGroupMenuRef)}
        >
          <DropdownSearchBar
            changeSearchText={setRestaurantGroupSearch}
            placeholder="Search restaurant group"
            isFetching={restaurantGroupFetching}
          />
          {displayedRestaurantGroups.map((group) => (
            <StyledMenuItem key={group.label} value={group.label}>
              {value.restaurant_group_id === group.label ? (
                <SelectedMenuItem data={group.value} />
              ) : (
                group.value
              )}
            </StyledMenuItem>
          ))}
          <DropdownInfiniteLoader
            hasNextPage={hasNextRestaurantGroupsPage}
            targetRef={restaurantGroupLoaderRef}
          />
        </Select>
      </FormControl>

      <FormControl
        size="small"
        sx={{ minWidth: { xs: "100%", sm: "175px" } }}
        disabled={!hasGroupSelected}
      >
        <Select
          data-testid={`${testIdPrefix}-restaurant-sub-group`}
          value={value.restaurant_sub_group_id}
          renderValue={() => getSubGroupName(value.restaurant_sub_group_id)}
          onChange={(event) => emitSubGroup(event.target.value)}
          onClose={() => setTimeout(() => setRestaurantSubGroupSearch(""), 250)}
          sx={selectBaseStyles}
          endAdornment={clearAdornment(hasSubGroupSelected, () => emitSubGroup(ALL_FILTER_VALUE))}
          MenuProps={dropdownMenuProps(restaurantSubGroupMenuRef)}
        >
          <DropdownSearchBar
            changeSearchText={setRestaurantSubGroupSearch}
            placeholder="Search sub-group"
            isFetching={restaurantSubGroupFetching}
          />
          {displayedSubGroups.map((subGroup) => (
            <StyledMenuItem key={subGroup.value} value={subGroup.value}>
              {`${value.restaurant_sub_group_id}` === `${subGroup.value}` ? (
                <SelectedMenuItem data={subGroup.label} />
              ) : (
                subGroup.label
              )}
            </StyledMenuItem>
          ))}
          <DropdownInfiniteLoader
            hasNextPage={hasNextRestaurantSubGroupsPage}
            targetRef={restaurantSubGroupLoaderRef}
          />
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "195px" } }}>
        <Select
          data-testid={`${testIdPrefix}-restaurant`}
          multiple
          value={value.restaurant_ids}
          renderValue={() => getRestaurantDisplayValue()}
          onChange={handleRestaurantChange}
          onClose={() => setTimeout(() => setRestaurantSearch(""), 250)}
          sx={selectBaseStyles}
          endAdornment={clearAdornment(!value.restaurant_ids.includes(ALL_FILTER_VALUE), () =>
            onChange({ ...value, restaurant_ids: [ALL_FILTER_VALUE] }),
          )}
          MenuProps={dropdownMenuProps(restaurantMenuRef)}
        >
          <DropdownSearchBar
            changeSearchText={setRestaurantSearch}
            placeholder="Search restaurant"
            isFetching={restaurantFetching}
          />
          {displayedRestaurants.map((restaurant) => (
            <StyledMenuItem key={restaurant.id} value={restaurant.id}>
              {value.restaurant_ids.includes(restaurant.id) ? (
                <SelectedMenuItem data={restaurant.name} />
              ) : (
                restaurant.name
              )}
            </StyledMenuItem>
          ))}
          <DropdownInfiniteLoader
            hasNextPage={hasNextRestaurantsPage}
            targetRef={restaurantLoaderRef}
          />
        </Select>
      </FormControl>
    </React.Fragment>
  );
};
